import {
  type ComponentProps,
  type ComponentType,
  createElement,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  components, type MultiValue, type MultiValueProps, type OptionProps, type SingleValue,
} from 'react-select';
import { toast } from 'react-toastify';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import { ReactComponent as ChartIcon } from 'assets/icons/chart.svg';
import { ReactComponent as CloudIcon } from 'assets/icons/cloud.svg';
import { ReactComponent as CompaniesIcon } from 'assets/icons/companies.svg';
import { ReactComponent as DatabaseIcon } from 'assets/icons/database.svg';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as FactoryIcon } from 'assets/icons/factory.svg';
import { ReactComponent as HelpCircleIcon } from 'assets/icons/helpCircle.svg';
import { ReactComponent as HistoryIcon } from 'assets/icons/history.svg';
import { ReactComponent as NetworkIcon } from 'assets/icons/network.svg';
import { ReactComponent as PencilIcon } from 'assets/icons/pencil.svg';
import { ReactComponent as PieBarIcon } from 'assets/icons/pieBar.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { ReactComponent as SpeakerIcon } from 'assets/icons/speaker.svg';
import { ReactComponent as SupplierRegulationIcon } from 'assets/icons/supplierRegulation.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/tresh.svg';
import { ReactComponent as UndoIcon } from 'assets/icons/undo.svg';
import Button from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import CustomPagination from 'components/CustomPagination/CustomPagination';
import EmptyList from 'components/EmptyList/EmptyList';
import IconButton from 'components/IconButton/IconButton';
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import Panel from 'components/Panel/Panel';
import Select from 'components/Select/Select';
import Title from 'components/Title/Title';
import Toggle from 'components/Toggle/Toggle';
import Tooltip from 'components/Tooltip/Tooltip';
import { isAgentTemplateUiId } from 'constants/agentTemplateUiMap';
import { AppRoutes } from 'constants/AppRoutes';
import { DEFAULT_MAX_LIMITED } from 'constants/general';
import { BUTTON_VARIANT, USERS_ROLE } from 'constants/interfaces';
import {
  useCreateAgentTemplateMutation,
  useDeleteAgentTemplateMutation,
  useGetAgentModelsQuery,
  useGetAgentTemplateQuery,
  useGetAgentTemplatesQuery,
  useGetAgentTemplateVersionsQuery,
  useReplaceAgentTemplateCompaniesMutation,
  useRestoreAgentTemplateVersionMutation,
  useUpdateAgentTemplateSettingsMutation,
} from 'store/slices/agentTemplatesAdmin/apis/agentTemplatesAdminApi';
import { useGetCompaniesListQuery } from 'store/slices/companies/apis/companiesApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import WatchTowerHubIcon, { useWatchTowerGaze } from './WatchTowerHubIcon';

import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type {
  IAgentTemplate,
  IAgentTemplateVersion,
} from 'store/slices/agentTemplatesAdmin/interfaces/IAgentTemplate';

import styles from './scss/AgentsManagement.module.scss';

type Screen = 'hub' | 'templates' | 'create' | 'edit' | 'history' | 'analytics';
type AgentType =
  | 'bom'
  | 'supplier-investigation'
  | 'reputation-monitoring'
  | 'regulation-monitoring'
  | 'finance-monitoring'
  | 'climate-monitoring'
  | 'ownership-monitoring';

interface AgentTypeOption {
  id: AgentType,
  title: string,
  description: string,
}

const AGENT_TYPE_OPTIONS: AgentTypeOption[] = [
  {
    id: 'bom',
    title: 'BOM',
    description: 'Bill of Materials agent with inferred edge support',
  },
  {
    id: 'supplier-investigation',
    title: 'Supplier Investigation',
    description: 'Discovery and verification of suppliers across tiers',
  },
  {
    id: 'reputation-monitoring',
    title: 'Public Sentiment Monitoring',
    description: 'Tracks media and public perception signals related to selected entities',
  },
  {
    id: 'regulation-monitoring',
    title: 'Product Regulation Monitoring',
    description: 'Tracks evolving regulations and policy signals impacting selected products',
  },
  {
    id: 'finance-monitoring',
    title: 'Financial Stability Monitoring',
    description: 'Analyzes financial indicators to detect stability and risk patterns',
  },
  {
    id: 'climate-monitoring',
    title: 'Climate Monitoring',
    description: 'Monitors climate and disaster risks at suppliers, Own Ops, and BSA',
  },
  {
    id: 'ownership-monitoring',
    title: 'Ownership Monitoring',
    description: 'Traces beneficial owners, parent entities, and related-group contagion risks',
  },
];

const AGENT_TYPES = AGENT_TYPE_OPTIONS.map((option) => option.id);

interface SelectOption {
  label: string,
  value: string,
}

interface CompanyAssignment {
  companyId: number,
  companyName: string,
  assignedAt?: string,
  assignedBy?: string,
}

interface TemplateVersion {
  versionNumber: number,
  createdAt: string,
  savedBy: string,
  templateName: string,
  enabled: boolean,
  agentModelId: number | null,
  aiModel: string,
  supplierLookupFirstStep: boolean,
  minimumConfidence: string,
  minimumEvidenceUrls: string,
  description: string,
  prompt: string,
  allowInferredEdges: boolean,
  runVerificationAfterDiscovery: boolean,
}

interface TemplateRecord {
  id: string,
  agentType: AgentType,
  currentVersionNumber: number,
  companyAssignments: CompanyAssignment[],
  templateName: string,
  enabled: boolean,
  agentModelId: number | null,
  aiModel: string,
  supplierLookupFirstStep: boolean,
  minimumConfidence: string,
  minimumEvidenceUrls: string,
  description: string,
  prompt: string,
  allowInferredEdges: boolean,
  runVerificationAfterDiscovery: boolean,
  createdAt: string,
  savedBy: string,
}

interface TemplateDraft {
  agentType: AgentType | '',
  baseTemplateId: string,
  templateName: string,
  enabled: boolean,
  agentModelId: number | null,
  aiModel: string,
  supplierLookupFirstStep: boolean,
  minimumConfidence: string,
  minimumEvidenceUrls: string,
  description: string,
  prompt: string,
  allowInferredEdges: boolean,
  runVerificationAfterDiscovery: boolean,
  companyAssignments: CompanyAssignment[],
}

interface AnalyticsSummary {
  id: string,
  agentType: AgentType,
  companyName: string,
  cost: number,
  runs: number,
  averageTimeSeconds: number,
  successRate: number,
}

interface AnalyticsRun {
  id: string,
  agentType: AgentType,
  companyName: string,
  tokens: number,
  cost: number,
  status: 'success' | 'failed',
  message: string,
  templateVersion: string,
  runAt: string,
}

interface AnalyticsMockState {
  analyticsSummary: AnalyticsSummary[],
  analyticsRuns: AnalyticsRun[],
}

const LEGACY_STORAGE_KEY = 'attaima-agents-management-ui-mock-v4';
const ANALYTICS_STORAGE_KEY = 'attaima-agents-management-analytics-mock-v1';
const PAGE_SIZE = 5;
const ANALYTICS_PAGE_SIZE = 5;
const DEFAULT_EMAIL = 'admin@attaima.com';
const MOCK_ANALYTICS_COMPANIES = ['Acme Corp', 'Nordic Supply', 'GlobalTech', 'Company A', 'Company B', 'Company C'];
const STATUS_OPTIONS: SelectOption[] = [
  { label: 'All status', value: 'all' },
  { label: 'Enabled', value: 'enabled' },
  { label: 'Disabled', value: 'disabled' },
];
const AGENT_TYPE_FILTER_OPTIONS: SelectOption[] = AGENT_TYPE_OPTIONS.map((option) => ({
  label: option.title,
  value: option.id,
}));

const FILTER_MULTI_VISIBLE_CHIPS = 2;

const ALLOW_INFERRED_CONNECTIONS_LABEL = (
  'Allow AI to use reasoning to suggest connections not found in direct search results'
);
const ALLOW_INFERRED_CONNECTIONS_HINT = [
  'When enabled, after LLM/API search the agent may still propose BOM connections that no search result',
  'explicitly states.',
  'Example: search finds “Part X is sold by Acme” and “Part Y is commonly paired with Acme parts,”',
  'so the AI links Part Y to Acme even though no page lists that supplier relationship.',
  'When disabled, only connections backed by search evidence are kept.',
].join(' ');

const ReactSelectOption = components.Option as unknown as ComponentType<OptionProps<SelectOption, boolean>>;
const ReactSelectMultiValue = components.MultiValue as unknown as ComponentType<MultiValueProps<SelectOption, true>>;

function AllowInferredConnectionsField({
  checked,
  id,
  onChange,
  readOnly = false,
}: {
  checked: boolean,
  id: string,
  onChange?: (checked: boolean) => void,
  readOnly?: boolean,
}): ReactElement {
  return (
    <div className={styles.checkbox}>
      <label className={styles.checkboxControl} htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          readOnly={readOnly}
          onChange={readOnly || !onChange ? undefined : (e) => onChange(e.target.checked)}
        />
        <span>{ALLOW_INFERRED_CONNECTIONS_LABEL}</span>
      </label>
      <Tooltip
        className={styles.helpTooltip}
        hint={ALLOW_INFERRED_CONNECTIONS_HINT}
        hintClassName={styles.fieldHint}
      >
        <span className={styles.helpIcon} aria-label="More information about inferred connections">
          <HelpCircleIcon />
        </span>
      </Tooltip>
    </div>
  );
}

AllowInferredConnectionsField.defaultProps = {
  onChange: undefined,
  readOnly: false,
};

function CheckboxSelectOption(props: OptionProps<SelectOption, boolean>) {
  const { data, isSelected, label } = props;

  return createElement(
    ReactSelectOption,
    props,
    (
      <div className={styles.checkboxOption}>
        <Checkbox
          name={`select-option-${data.value}`}
          value={data.value}
          checked={isSelected}
          onChange={() => undefined}
          showSquare
          size="small"
          inputAriaLabel={label}
        />
        <span>{label}</span>
      </div>
    ),
  );
}

function CompactFilterMultiValue(props: MultiValueProps<SelectOption, true>) {
  const { index, getValue } = props;
  const selectedCount = getValue().length;
  const hiddenCount = selectedCount - FILTER_MULTI_VISIBLE_CHIPS;

  if (index >= FILTER_MULTI_VISIBLE_CHIPS) {
    if (index === FILTER_MULTI_VISIBLE_CHIPS && hiddenCount > 0) {
      return <span className={styles.moreCount}>{`+${hiddenCount}`}</span>;
    }
    return null;
  }

  return createElement(ReactSelectMultiValue, props);
}

// Shared Select typing is untyped/`unknown`; cast keeps checkbox Option usable.
const checkboxMultiSelectComponents = {
  Option: CheckboxSelectOption,
} as NonNullable<ComponentProps<typeof Select>['components']>;

const filterMultiSelectComponents = {
  Option: CheckboxSelectOption,
  MultiValue: CompactFilterMultiValue,
} as NonNullable<ComponentProps<typeof Select>['components']>;

const formatAgentType = (agentType: AgentType): string => (
  AGENT_TYPE_OPTIONS.find((option) => option.id === agentType)?.title || agentType
);

const agentTypeIcon = (agentType: AgentType): ReactElement => {
  const className = styles.selectCardIcon;
  switch (agentType) {
    case 'bom':
      return <NetworkIcon className={className} />;
    case 'supplier-investigation':
      return <SearchIcon className={className} />;
    case 'reputation-monitoring':
      return <SpeakerIcon className={className} />;
    case 'regulation-monitoring':
      return <SupplierRegulationIcon className={className} />;
    case 'finance-monitoring':
      return <ChartIcon className={className} />;
    case 'climate-monitoring':
      return <CloudIcon className={className} />;
    case 'ownership-monitoring':
      return <FactoryIcon className={className} />;
    default:
      return <NetworkIcon className={className} />;
  }
};

const nowIso = (): string => new Date().toISOString();
const formatDateTime = (value: string): string => new Date(value).toLocaleString();
const formatDuration = (seconds: number): string => (
  seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`
);
const formatCurrency = (value: number): string => `$${value.toFixed(2)}`;
const companyTokens = (assignments?: CompanyAssignment[] | null): string[] => (
  assignments?.length ? assignments.map((item) => item.companyName) : ['All companies']
);

function DiffText({
  before,
  after,
  multiline = false,
}: {
  before: string,
  after: string,
  multiline?: boolean,
}): ReactElement {
  if (before === after) {
    return <span className={multiline ? styles.diffPromptPlain : undefined}>{after || '—'}</span>;
  }
  if (multiline) {
    return (
      <div className={styles.diffPromptStack}>
        <p className={styles.diffRemoved}>{before || '—'}</p>
        <p className={styles.diffAdded}>{after || '—'}</p>
      </div>
    );
  }
  return (
    <span className={styles.diffInline}>
      <span className={styles.diffRemoved}>{before || '—'}</span>
      {' '}
      <span className={styles.diffAdded}>{after || '—'}</span>
    </span>
  );
}

DiffText.defaultProps = {
  multiline: false,
};

const emptyDraft = (): TemplateDraft => ({
  agentType: '',
  baseTemplateId: '',
  templateName: '',
  enabled: false,
  agentModelId: null,
  aiModel: '',
  supplierLookupFirstStep: false,
  minimumConfidence: '0.70',
  minimumEvidenceUrls: '1',
  description: '',
  prompt: '',
  allowInferredEdges: false,
  runVerificationAfterDiscovery: true,
  companyAssignments: [],
});

const recordToVersion = (template: TemplateRecord): TemplateVersion => ({
  versionNumber: template.currentVersionNumber,
  createdAt: template.createdAt,
  savedBy: template.savedBy,
  templateName: template.templateName,
  enabled: template.enabled,
  agentModelId: template.agentModelId,
  aiModel: template.aiModel,
  supplierLookupFirstStep: template.supplierLookupFirstStep,
  minimumConfidence: template.minimumConfidence,
  minimumEvidenceUrls: template.minimumEvidenceUrls,
  description: template.description,
  prompt: template.prompt,
  allowInferredEdges: template.allowInferredEdges,
  runVerificationAfterDiscovery: template.runVerificationAfterDiscovery,
});

const mapApiCompanies = (
  companies: IAgentTemplate['companies'],
): CompanyAssignment[] => (
  companies === 'all'
    ? []
    : companies.map((company) => ({
      companyId: company.id,
      companyName: company.name,
    }))
);

const mapApiTemplateToRecord = (template: IAgentTemplate): TemplateRecord => ({
  id: String(template.id),
  agentType: (isAgentTemplateUiId(template.uiAgentType)
    ? template.uiAgentType
    : 'bom') as AgentType,
  currentVersionNumber: template.promptVersion,
  companyAssignments: mapApiCompanies(template.companies),
  templateName: template.name,
  enabled: template.enabled,
  agentModelId: template.agentModelId ?? null,
  aiModel: template.model,
  supplierLookupFirstStep: template.supplierLookupFirstStep,
  minimumConfidence: Number(template.minimumConfidence).toFixed(2),
  minimumEvidenceUrls: `${Math.trunc(template.minimumEvidenceUrls)}`,
  description: template.description || '',
  prompt: template.promptTemplate || '',
  allowInferredEdges: template.allowInferredEdges ?? false,
  runVerificationAfterDiscovery: template.runVerificationAfterDiscovery ?? true,
  createdAt: template.currentVersion?.createdAt || template.createdAt,
  savedBy: template.currentVersion?.savedByEmail || DEFAULT_EMAIL,
});

const mapApiVersionToTemplateVersion = (version: IAgentTemplateVersion): TemplateVersion => ({
  versionNumber: version.versionNumber,
  createdAt: version.createdAt,
  savedBy: version.savedByEmail || DEFAULT_EMAIL,
  templateName: version.name,
  enabled: version.enabled,
  agentModelId: version.agentModelId ?? null,
  aiModel: version.model,
  supplierLookupFirstStep: version.supplierLookupFirstStep,
  minimumConfidence: Number(version.minimumConfidence).toFixed(2),
  minimumEvidenceUrls: `${Math.trunc(version.minimumEvidenceUrls)}`,
  description: version.description || '',
  prompt: version.promptTemplate || '',
  allowInferredEdges: version.allowInferredEdges ?? false,
  runVerificationAfterDiscovery: version.runVerificationAfterDiscovery ?? true,
});

const settingsPayloadFromDraft = (draft: TemplateDraft) => ({
  name: draft.templateName.trim(),
  agentModelId: draft.agentModelId as number,
  enabled: draft.enabled,
  description: draft.description.trim() || null,
  promptTemplate: draft.prompt.trim(),
  supplierLookupFirstStep: draft.supplierLookupFirstStep,
  minimumConfidence: Number(draft.minimumConfidence),
  minimumEvidenceUrls: Number(draft.minimumEvidenceUrls),
  allowInferredEdges: draft.allowInferredEdges,
  runVerificationAfterDiscovery: draft.runVerificationAfterDiscovery,
});

const isNoChangesError = (error: unknown): boolean => (
  typeof error === 'object'
  && error !== null
  && 'status' in error
  && (error as FetchBaseQueryError).status === 400
);

const createInitialAnalyticsState = (): AnalyticsMockState => ({
  analyticsSummary: [
    {
      id: 's1',
      agentType: 'bom',
      companyName: 'Acme Corp',
      cost: 312.4,
      runs: 847,
      averageTimeSeconds: 35,
      successRate: 95.1,
    },
    {
      id: 's2',
      agentType: 'bom',
      companyName: 'GlobalTech',
      cost: 194.2,
      runs: 512,
      averageTimeSeconds: 39,
      successRate: 91.2,
    },
    {
      id: 's3',
      agentType: 'supplier-investigation',
      companyName: 'Nordic Supply',
      cost: 498.5,
      runs: 1322,
      averageTimeSeconds: 36,
      successRate: 95.1,
    },
    {
      id: 's4',
      agentType: 'supplier-investigation',
      companyName: 'Company B',
      cost: 156.8,
      runs: 218,
      averageTimeSeconds: 95,
      successRate: 89.7,
    },
    {
      id: 's5',
      agentType: 'bom',
      companyName: 'Company A',
      cost: 128.12,
      runs: 278,
      averageTimeSeconds: 41,
      successRate: 88.4,
    },
    {
      id: 's6',
      agentType: 'reputation-monitoring',
      companyName: 'Acme Corp',
      cost: 210.4,
      runs: 420,
      averageTimeSeconds: 48,
      successRate: 93.2,
    },
    {
      id: 's7',
      agentType: 'regulation-monitoring',
      companyName: 'Company A',
      cost: 188.9,
      runs: 305,
      averageTimeSeconds: 52,
      successRate: 90.5,
    },
    {
      id: 's8',
      agentType: 'finance-monitoring',
      companyName: 'Nordic Supply',
      cost: 276.1,
      runs: 390,
      averageTimeSeconds: 44,
      successRate: 92.0,
    },
    {
      id: 's9',
      agentType: 'climate-monitoring',
      companyName: 'GlobalTech',
      cost: 165.3,
      runs: 241,
      averageTimeSeconds: 58,
      successRate: 88.8,
    },
    {
      id: 's10',
      agentType: 'ownership-monitoring',
      companyName: 'GlobalTech',
      cost: 142.7,
      runs: 198,
      averageTimeSeconds: 61,
      successRate: 87.4,
    },
  ],
  analyticsRuns: [
    {
      id: 'run-1001',
      agentType: 'bom',
      companyName: 'Acme Corp',
      tokens: 1821,
      cost: 8.122341,
      status: 'success',
      message: 'Completed successfully',
      templateVersion: 'V2',
      runAt: '2026-08-02T09:34:00.000Z',
    },
    {
      id: 'run-1002',
      agentType: 'bom',
      companyName: 'GlobalTech',
      tokens: 1456,
      cost: 6.223112,
      status: 'failed',
      message: 'Evidence threshold not met',
      templateVersion: 'V2',
      runAt: '2026-08-02T10:18:00.000Z',
    },
    {
      id: 'run-1003',
      agentType: 'supplier-investigation',
      companyName: 'Nordic Supply',
      tokens: 3122,
      cost: 15.204111,
      status: 'success',
      message: 'Completed successfully',
      templateVersion: 'V1',
      runAt: '2026-08-03T14:03:00.000Z',
    },
    {
      id: 'run-1004',
      agentType: 'reputation-monitoring',
      companyName: 'Acme Corp',
      tokens: 2201,
      cost: 9.441200,
      status: 'success',
      message: 'Completed successfully',
      templateVersion: 'V1',
      runAt: '2026-08-04T11:12:00.000Z',
    },
    {
      id: 'run-1005',
      agentType: 'finance-monitoring',
      companyName: 'Nordic Supply',
      tokens: 1988,
      cost: 7.880100,
      status: 'success',
      message: 'Completed successfully',
      templateVersion: 'V1',
      runAt: '2026-08-05T08:40:00.000Z',
    },
  ],
});

const loadAnalyticsState = (): AnalyticsMockState => {
  if (typeof window === 'undefined') return createInitialAnalyticsState();
  const raw = window.localStorage.getItem(ANALYTICS_STORAGE_KEY);
  if (raw) {
    try {
      return {
        analyticsSummary: (JSON.parse(raw) as AnalyticsMockState).analyticsSummary || [],
        analyticsRuns: (JSON.parse(raw) as AnalyticsMockState).analyticsRuns || [],
      };
    } catch {
      return createInitialAnalyticsState();
    }
  }
  const legacyRaw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
  if (legacyRaw) {
    try {
      const legacy = JSON.parse(legacyRaw) as {
        analyticsSummary?: AnalyticsSummary[],
        analyticsRuns?: AnalyticsRun[],
      };
      return {
        analyticsSummary: legacy.analyticsSummary || [],
        analyticsRuns: legacy.analyticsRuns || [],
      };
    } catch {
      return createInitialAnalyticsState();
    }
  }
  return createInitialAnalyticsState();
};

const draftFromVersion = (
  version: TemplateVersion,
  agentType: AgentType,
  companyAssignments: CompanyAssignment[] = [],
  baseTemplateId = '',
): TemplateDraft => ({
  agentType,
  baseTemplateId,
  templateName: version.templateName,
  enabled: version.enabled,
  agentModelId: version.agentModelId,
  aiModel: version.aiModel,
  supplierLookupFirstStep: version.supplierLookupFirstStep,
  minimumConfidence: version.minimumConfidence,
  minimumEvidenceUrls: version.minimumEvidenceUrls,
  description: version.description,
  prompt: version.prompt,
  allowInferredEdges: version.allowInferredEdges,
  runVerificationAfterDiscovery: version.runVerificationAfterDiscovery,
  companyAssignments,
});

const versionFromDraft = (draft: TemplateDraft, versionNumber: number, savedBy: string): TemplateVersion => ({
  versionNumber,
  createdAt: nowIso(),
  savedBy,
  templateName: draft.templateName.trim(),
  enabled: draft.enabled,
  agentModelId: draft.agentModelId,
  aiModel: draft.aiModel,
  supplierLookupFirstStep: draft.supplierLookupFirstStep,
  minimumConfidence: Number(draft.minimumConfidence).toFixed(2),
  minimumEvidenceUrls: `${Math.trunc(Number(draft.minimumEvidenceUrls))}`,
  description: draft.description.trim(),
  prompt: draft.prompt.trim(),
  allowInferredEdges: draft.allowInferredEdges,
  runVerificationAfterDiscovery: draft.runVerificationAfterDiscovery,
});

/** Compares versioned settings only (company assignments are template-scoped). */
const hasVersionChanges = (draft: TemplateDraft, current: TemplateVersion): boolean => {
  const next = versionFromDraft(draft, current.versionNumber, current.savedBy);
  return next.templateName !== current.templateName
    || next.enabled !== current.enabled
    || next.agentModelId !== current.agentModelId
    || next.aiModel !== current.aiModel
    || next.supplierLookupFirstStep !== current.supplierLookupFirstStep
    || next.minimumConfidence !== current.minimumConfidence
    || next.minimumEvidenceUrls !== current.minimumEvidenceUrls
    || next.description !== current.description
    || next.prompt !== current.prompt
    || next.allowInferredEdges !== current.allowInferredEdges
    || next.runVerificationAfterDiscovery !== current.runVerificationAfterDiscovery;
};

const downloadCsv = (filename: string, rows: Array<Array<string | number>>) => {
  const csv = rows
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

function AgentsManagement(): ReactElement {
  const navigate = useNavigate();
  const watchTowerIconRef = useRef<HTMLSpanElement>(null);
  const { pupil, gazeProps } = useWatchTowerGaze(watchTowerIconRef);
  const { data: me, isFetching } = useUserMeQuery();
  const currentEmail = me?.data.email || DEFAULT_EMAIL;
  const [screen, setScreen] = useState<Screen>('hub');
  const [analyticsState] = useState<AnalyticsMockState>(loadAnalyticsState);
  const [templateAgentTypes, setTemplateAgentTypes] = useState<AgentType[]>([]);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateStatus, setTemplateStatus] = useState<'all' | 'enabled' | 'disabled'>('all');
  const [templateCompanyFilter, setTemplateCompanyFilter] = useState<string[]>([]);
  const [templatePage, setTemplatePage] = useState(1);
  const [analyticsPage, setAnalyticsPage] = useState(1);
  const [analyticsAgentType, setAnalyticsAgentType] = useState<'all' | AgentType>('all');
  const [analyticsCompany, setAnalyticsCompany] = useState('all');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [createStep, setCreateStep] = useState(1);
  const [baseSearch, setBaseSearch] = useState('');
  const [baseSortField, setBaseSortField] = useState<
  'templateName' | 'aiModel' | 'versionNumber' | 'minimumConfidence' | 'minimumEvidenceUrls'
  >('templateName');
  const [baseSortDir, setBaseSortDir] = useState<'asc' | 'desc'>('asc');
  const [draft, setDraft] = useState<TemplateDraft>(emptyDraft());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [companiesModalOpen, setCompaniesModalOpen] = useState(false);
  const [companiesDraft, setCompaniesDraft] = useState<CompanyAssignment[]>([]);
  const [pendingCompanies, setPendingCompanies] = useState<string[]>([]);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [historyVersion, setHistoryVersion] = useState<number | null>(null);
  const [historyCompareTo, setHistoryCompareTo] = useState<number | null>(null);

  const { data: companiesData } = useGetCompaniesListQuery({
    limit: DEFAULT_MAX_LIMITED,
    offset: 0,
  });

  const shouldLoadAgentModels = screen === 'create' || screen === 'edit';
  const agentModelsIncludeId = draft.agentModelId ?? undefined;
  const {
    data: agentModelsResponse,
    isFetching: isAgentModelsLoading,
    isError: isAgentModelsError,
  } = useGetAgentModelsQuery(
    agentModelsIncludeId != null ? { includeId: agentModelsIncludeId } : undefined,
    { skip: !shouldLoadAgentModels },
  );

  const agentModelOptions = useMemo(() => {
    const items = [...(agentModelsResponse?.data ?? [])];
    items.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    return items;
  }, [agentModelsResponse]);

  useEffect(() => {
    if (!shouldLoadAgentModels || draft.agentModelId != null || !agentModelOptions.length) {
      return;
    }
    const first = agentModelOptions[0];
    setDraft((prev) => {
      if (prev.agentModelId != null) {
        return prev;
      }
      return {
        ...prev,
        agentModelId: first.id,
        aiModel: first.name,
      };
    });
  }, [shouldLoadAgentModels, draft.agentModelId, agentModelOptions]);

  const companyOptions = useMemo<SelectOption[]>(
    () => (companiesData?.data.items || []).map((company) => ({
      label: company.name,
      value: String(company.id),
    })),
    [companiesData],
  );

  const templateListParams = useMemo(() => ({
    offset: (templatePage - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
    q: templateSearch.trim() || undefined,
    uiAgentTypes: templateAgentTypes.length ? templateAgentTypes : undefined,
    enabled: templateStatus === 'all'
      ? undefined
      : templateStatus === 'enabled',
    companyIds: templateCompanyFilter.length
      ? templateCompanyFilter.map((companyId) => Number(companyId))
      : undefined,
  }), [templatePage, templateSearch, templateAgentTypes, templateStatus, templateCompanyFilter]);

  const {
    data: templatesResponse,
    isFetching: isTemplatesLoading,
    isError: isTemplatesError,
  } = useGetAgentTemplatesQuery(templateListParams, {
    skip: screen !== 'templates',
  });

  const baseTemplateListParams = useMemo(() => ({
    offset: 0,
    limit: 100,
    uiAgentTypes: draft.agentType ? [draft.agentType] : undefined,
  }), [draft.agentType]);

  const { data: baseTemplatesResponse, isFetching: isBaseTemplatesLoading } = useGetAgentTemplatesQuery(
    baseTemplateListParams,
    { skip: screen !== 'create' || createStep !== 2 || !draft.agentType },
  );

  const selectedTemplateIdNum = selectedTemplateId ? Number(selectedTemplateId) : undefined;

  const {
    data: templateDetailResponse,
    isFetching: isTemplateDetailLoading,
    isError: isTemplateDetailError,
  } = useGetAgentTemplateQuery(
    { id: selectedTemplateIdNum! },
    { skip: !selectedTemplateIdNum || (screen !== 'edit' && screen !== 'history') },
  );

  const {
    data: templateVersionsResponse,
    isFetching: isTemplateVersionsLoading,
    isError: isTemplateVersionsError,
  } = useGetAgentTemplateVersionsQuery(selectedTemplateIdNum!, {
    skip: !selectedTemplateIdNum || screen !== 'history',
  });

  const [createAgentTemplate, { isLoading: isCreatingTemplate }] = useCreateAgentTemplateMutation();
  const [updateAgentTemplateSettings, { isLoading: isSavingTemplate }] = useUpdateAgentTemplateSettingsMutation();
  const [
    replaceAgentTemplateCompanies,
    { isLoading: isUpdatingCompanies },
  ] = useReplaceAgentTemplateCompaniesMutation();
  const [restoreAgentTemplateVersion, { isLoading: isRestoringVersion }] = useRestoreAgentTemplateVersionMutation();
  const [deleteAgentTemplate, { isLoading: isDeletingTemplate }] = useDeleteAgentTemplateMutation();

  const isTemplateMutationPending = isCreatingTemplate
    || isSavingTemplate
    || isUpdatingCompanies
    || isRestoringVersion
    || isDeletingTemplate;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(ANALYTICS_STORAGE_KEY, JSON.stringify(analyticsState));
    }
  }, [analyticsState]);

  useEffect(() => {
    if (isTemplatesError) {
      toast.error('Failed to load templates. Please try again.');
    }
  }, [isTemplatesError]);

  useEffect(() => {
    if (isTemplateDetailError) {
      toast.error('Failed to load template details. Please try again.');
    }
  }, [isTemplateDetailError]);

  useEffect(() => {
    if (isTemplateVersionsError) {
      toast.error('Failed to load template versions. Please try again.');
    }
  }, [isTemplateVersionsError]);

  const pagedTemplates = useMemo(
    () => (templatesResponse?.data.items || []).map(mapApiTemplateToRecord),
    [templatesResponse],
  );

  const templateTotalCount = templatesResponse?.data.meta.count ?? 0;
  const templateTotalPages = Math.max(1, Math.ceil(templateTotalCount / PAGE_SIZE));

  const selectedTemplate = useMemo(() => {
    if (templateDetailResponse?.data) {
      return mapApiTemplateToRecord(templateDetailResponse.data);
    }
    return pagedTemplates.find((template) => template.id === selectedTemplateId);
  }, [templateDetailResponse, pagedTemplates, selectedTemplateId]);

  const selectedCurrentVersion = selectedTemplate ? recordToVersion(selectedTemplate) : undefined;

  const baseTemplateRows = useMemo(
    () => (baseTemplatesResponse?.data.items || []).map((template) => ({
      template: mapApiTemplateToRecord(template),
      current: recordToVersion(mapApiTemplateToRecord(template)),
    })),
    [baseTemplatesResponse],
  );

  const filteredBases = useMemo(() => {
    const searched = baseTemplateRows.filter(({ current }) => (
      !baseSearch.trim()
      || current.templateName.toLowerCase().includes(baseSearch.trim().toLowerCase())
    ));
    return [...searched].sort((a, b) => {
      const aVal = a.current[baseSortField];
      const bVal = b.current[baseSortField];
      const cmp = typeof aVal === 'string'
        ? aVal.localeCompare(bVal as string, undefined, { sensitivity: 'base' })
        : Number(aVal) - Number(bVal);
      return baseSortDir === 'asc' ? cmp : -cmp;
    });
  }, [baseTemplateRows, baseSearch, baseSortField, baseSortDir]);

  const toggleBaseSort = useCallback((field: typeof baseSortField) => {
    setBaseSortField((prev) => {
      if (prev === field) {
        setBaseSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setBaseSortDir('asc');
      }
      return field;
    });
  }, []);

  const menuPortalTarget = typeof document === 'undefined' ? undefined : document.body;

  const createAssignableCompanyOptions = useMemo(
    () => companyOptions.filter(
      (option) => !draft.companyAssignments.some((item) => String(item.companyId) === option.value),
    ),
    [companyOptions, draft.companyAssignments],
  );

  const modalAssignableCompanyOptions = useMemo(
    () => companyOptions.filter(
      (option) => !companiesDraft.some((item) => String(item.companyId) === option.value),
    ),
    [companyOptions, companiesDraft],
  );

  const addPendingCompanies = (
    currentAssignments: CompanyAssignment[],
    companyIds: string[],
  ): CompanyAssignment[] => {
    const existing = new Set(currentAssignments.map((item) => item.companyId));
    const additions = companyIds
      .filter((companyId) => !existing.has(Number(companyId)))
      .map((companyId) => {
        const option = companyOptions.find((item) => item.value === companyId);
        return {
          companyId: Number(companyId),
          companyName: option?.label || companyId,
          assignedAt: nowIso(),
          assignedBy: currentEmail,
        };
      });

    return [...currentAssignments, ...additions];
  };

  const filteredSummary = analyticsState.analyticsSummary.filter((item) => (
    (analyticsAgentType === 'all' || item.agentType === analyticsAgentType)
    && (analyticsCompany === 'all' || item.companyName === analyticsCompany)
  ));
  const pagedSummary = filteredSummary.slice(
    (analyticsPage - 1) * ANALYTICS_PAGE_SIZE,
    analyticsPage * ANALYTICS_PAGE_SIZE,
  );
  const analyticsTotalPages = Math.max(1, Math.ceil(filteredSummary.length / ANALYTICS_PAGE_SIZE));
  const filteredRuns = analyticsState.analyticsRuns.filter((item) => (
    (analyticsAgentType === 'all' || item.agentType === analyticsAgentType)
    && (analyticsCompany === 'all' || item.companyName === analyticsCompany)
  ));

  const historyVersions = useMemo(
    () => [...(templateVersionsResponse?.data || [])]
      .map(mapApiVersionToTemplateVersion)
      .sort((a, b) => b.versionNumber - a.versionNumber),
    [templateVersionsResponse],
  );
  const currentHistoryVersion = historyVersions.find(
    (item) => item.versionNumber === historyVersion,
  ) || historyVersions[0];
  const compareOptions = historyVersions.filter(
    (item) => item.versionNumber !== currentHistoryVersion?.versionNumber,
  );
  const currentCompareVersion = compareOptions.find(
    (item) => item.versionNumber === historyCompareTo,
  ) || compareOptions[0];

  useEffect(() => { setTemplatePage(1); }, [templateAgentTypes, templateSearch, templateStatus, templateCompanyFilter]);
  useEffect(() => { setAnalyticsPage(1); }, [analyticsAgentType, analyticsCompany]);
  useEffect(() => {
    if (screen !== 'edit' || !templateDetailResponse?.data) return;
    if (String(templateDetailResponse.data.id) !== selectedTemplateId) return;
    const record = mapApiTemplateToRecord(templateDetailResponse.data);
    setDraft(draftFromVersion(
      recordToVersion(record),
      record.agentType,
      record.companyAssignments,
    ));
    setErrors({});
  }, [screen, selectedTemplateId, templateDetailResponse?.data]);
  useEffect(() => {
    if (screen !== 'history' || !historyVersions.length) return;
    setHistoryVersion(historyVersions[0].versionNumber);
    setHistoryCompareTo(historyVersions[1]?.versionNumber || null);
  }, [screen, selectedTemplateId, historyVersions]);

  const validateDraft = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!draft.templateName.trim()) nextErrors.templateName = 'Required';
    if (!draft.agentModelId) nextErrors.aiModel = 'Required';
    if (!draft.prompt.trim()) nextErrors.prompt = 'Required';
    if (
      Number.isNaN(Number(draft.minimumConfidence))
      || Number(draft.minimumConfidence) < 0
      || Number(draft.minimumConfidence) > 1
    ) {
      nextErrors.minimumConfidence = 'Use 0.00 to 1.00';
    }
    if (!/^\d+$/.test(draft.minimumEvidenceUrls) || Number(draft.minimumEvidenceUrls) < 0) {
      nextErrors.minimumEvidenceUrls = 'Use an integer >= 0';
    }
    setErrors(nextErrors);
    return !Object.keys(nextErrors).length;
  };

  const goToTemplatesList = (agentType: AgentType) => {
    setTemplateAgentTypes([agentType]);
    setTemplatePage(1);
    setScreen('templates');
  };

  const createTemplate = async () => {
    if (isCreatingTemplate) return;
    try {
      const agentType = draft.agentType as AgentType;
      await createAgentTemplate({
        uiAgentType: agentType,
        ...settingsPayloadFromDraft(draft),
        companyIds: draft.companyAssignments.length
          ? draft.companyAssignments.map((item) => item.companyId)
          : undefined,
        baseTemplateId: draft.baseTemplateId ? Number(draft.baseTemplateId) : undefined,
      }).unwrap();
      setCreateStep(1);
      setDraft(emptyDraft());
      setBaseSearch('');
      setPendingCompanies([]);
      goToTemplatesList(agentType);
      toast.success('Template created successfully.');
    } catch {
      toast.error('Failed to create template. Please try again.');
    }
  };

  const saveTemplateVersion = async (baseDraft: TemplateDraft): Promise<boolean> => {
    if (!selectedTemplate || !selectedTemplateIdNum || isSavingTemplate) {
      toast.error('Failed to save template. Please try again.');
      return false;
    }
    try {
      const result = await updateAgentTemplateSettings({
        id: selectedTemplateIdNum,
        body: settingsPayloadFromDraft(baseDraft),
      }).unwrap();
      toast.success(`Template saved as V${result.data.promptVersion}.`);
      return true;
    } catch (error) {
      if (isNoChangesError(error)) {
        toast.info('No changes to save. A new version was not created.');
        return false;
      }
      toast.error('Failed to save template. Please try again.');
      return false;
    }
  };

  /** Company access is template-scoped; updates never create a new version. */
  const updateCompanyAssignments = async (companyAssignments: CompanyAssignment[]): Promise<boolean> => {
    if (!selectedTemplate || !selectedTemplateIdNum || isUpdatingCompanies) {
      toast.error('Failed to update company assignments. Please try again.');
      return false;
    }
    try {
      await replaceAgentTemplateCompanies({
        id: selectedTemplateIdNum,
        companyIds: companyAssignments.map((item) => item.companyId),
      }).unwrap();
      setDraft((prev) => ({ ...prev, companyAssignments }));
      toast.success('Company assignments updated successfully.');
      return true;
    } catch {
      toast.error('Failed to update company assignments. Please try again.');
      return false;
    }
  };

  const restoreTemplateVersion = async (): Promise<boolean> => {
    if (!selectedTemplate || !selectedTemplateIdNum || !currentHistoryVersion || isRestoringVersion) {
      toast.error('Failed to restore template version. Please try again.');
      return false;
    }
    try {
      const result = await restoreAgentTemplateVersion({
        id: selectedTemplateIdNum,
        versionNumber: currentHistoryVersion.versionNumber,
      }).unwrap();
      toast.success(`Template restored as V${result.data.promptVersion}.`);
      return true;
    } catch {
      toast.error('Failed to restore template version. Please try again.');
      return false;
    }
  };

  const deleteSelectedTemplate = async (): Promise<boolean> => {
    if (!selectedTemplate || !selectedTemplateIdNum || isDeletingTemplate) {
      toast.error('Failed to delete template. Please try again.');
      return false;
    }
    try {
      const result = await deleteAgentTemplate(selectedTemplateIdNum).unwrap();
      const affectedCount = result.data.affectedScheduleCount;
      toast.success(
        affectedCount
          ? `Template deleted. ${affectedCount} schedule${affectedCount === 1 ? '' : 's'} affected.`
          : 'Template deleted successfully.',
      );
      setSelectedTemplateId('');
      setDeleteModalOpen(false);
      setScreen('templates');
      return true;
    } catch {
      toast.error('Failed to delete template. Please try again.');
      return false;
    }
  };

  if (isFetching) return <Loader isPageLoading />;
  if (me?.data.role !== USERS_ROLE.admin) return <Navigate to={AppRoutes.launchpad} replace />;

  const analyticsCards = (agentType: AgentType) => {
    const items = analyticsState.analyticsSummary.filter((item) => item.agentType === agentType);
    const cost = items.reduce((sum, item) => sum + item.cost, 0);
    const runs = items.reduce((sum, item) => sum + item.runs, 0);
    const avg = items.length
      ? Math.round(items.reduce((sum, item) => sum + item.averageTimeSeconds, 0) / items.length)
      : 0;
    const success = runs ? items.reduce((sum, item) => sum + item.successRate * item.runs, 0) / runs : 0;
    const ok = Math.round((success / 100) * runs);
    const failed = Math.max(runs - ok, 0);
    return [
      ['Total cost', formatCurrency(cost)],
      ['Quantity', `${runs}`],
      ['Average time', formatDuration(avg)],
      ['Longest time', formatDuration(items.reduce((max, item) => Math.max(max, item.averageTimeSeconds), 0))],
      ['Failed runs', `${failed} (${runs ? ((failed / runs) * 100).toFixed(1) : '0.0'}%)`],
      ['Successful runs', `${ok} (${success.toFixed(1)}%)`],
    ];
  };

  const historyBefore = currentCompareVersion || currentHistoryVersion;
  const historyAfter = currentHistoryVersion;
  const hasTemplateRows = !isTemplatesLoading && pagedTemplates.length > 0;
  const shouldShowTemplatesEmptyState = !isTemplatesLoading && !pagedTemplates.length;
  const shouldShowBaseTemplateBrowser = baseTemplateRows.length > 0 || isBaseTemplatesLoading;

  return (
    <div className={styles.root}>
      <div className={styles.wrapper}>
        <div className={styles.header}>
          {screen !== 'hub' ? (
            <button
              type="button"
              className={styles.backButton}
              onClick={() => {
                if (screen === 'create' && createStep > 1) { setCreateStep((prev) => prev - 1); return; }
                if (screen === 'templates' || screen === 'analytics' || screen === 'create') {
                  setScreen('hub');
                  return;
                }
                setScreen('templates');
              }}
            >
              <ArrowLeftIcon className={styles.backButtonIcon} />
              <span>Back</span>
            </button>
          ) : null}
          {screen === 'hub' ? (
            <div className={styles.pageHeading}>
              <Title className={styles.pageTitle} text="Agents Management" />
            </div>
          ) : null}
          {screen === 'templates' ? (
            <div className={styles.pageHeading}>
              <Title className={styles.pageTitle} text="Templates" />
              <Button
                title="New template"
                startIcon={<PlusIcon />}
                onClick={() => {
                  setDraft(emptyDraft());
                  setCreateStep(1);
                  setScreen('create');
                }}
              />
            </div>
          ) : null}
          {screen === 'analytics' ? (
            <div className={styles.pageHeading}>
              <Title className={styles.pageTitle} text="Analytics" />
            </div>
          ) : null}
          {screen === 'create' ? (
            <div className={styles.pageHeading}>
              <Title className={styles.pageTitle} text="Create a template" />
            </div>
          ) : null}
          {screen === 'history' ? (
            <div className={styles.pageHeading}>
              <Title className={styles.pageTitle} text="Version history" />
            </div>
          ) : null}
        </div>

        {screen === 'hub' ? (
          <div className={styles.cardGrid}>
            <Panel className={styles.hubCard}>
              <DatabaseIcon className={styles.cardIcon} />
              <h2>Templates</h2>
              <p>Configure and control templates available to your companies.</p>
              <Button title="Open templates" onClick={() => setScreen('templates')} />
            </Panel>
            <Panel className={styles.hubCard}>
              <PieBarIcon className={styles.cardIcon} />
              <h2>Analytics</h2>
              <p>Track and monitor agent performance across companies.</p>
              <Button
                title="Open analytics"
                variant={BUTTON_VARIANT.secondary}
                onClick={() => setScreen('analytics')}
              />
            </Panel>
            <div
              className={styles.watchTowerCard}
              onMouseMove={gazeProps.onMouseMove}
              onMouseLeave={gazeProps.onMouseLeave}
            >
              <Panel className={styles.hubCard}>
                <WatchTowerHubIcon
                  ref={watchTowerIconRef}
                  pupilX={pupil.x}
                  pupilY={pupil.y}
                />
                <h2>Watch Tower</h2>
                <p>Register external companies. Schedule monitoring agents. Inspect runs in tables.</p>
                <Button title="Open Watch Tower" onClick={() => navigate(AppRoutes.watchTower)} />
              </Panel>
            </div>
            <Panel className={styles.hubCard}>
              <SpeakerIcon className={styles.cardIcon} />
              <h2>Marketing Ops</h2>
              <p>Read incidents, risks, and impacts. Find client opportunities.</p>
              <Button
                title="Open Marketing Ops"
                variant={BUTTON_VARIANT.secondary}
                onClick={() => navigate(AppRoutes.marketingOps)}
              />
            </Panel>
          </div>
        ) : null}

        {screen === 'templates' ? (
          <>
            <div className={styles.toolbar}>
              <div className={styles.filters}>
                <label className={styles.searchField} htmlFor="agent-template-search">
                  <SearchIcon />
                  <input
                    id="agent-template-search"
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    placeholder="Search templates"
                  />
                </label>
                <Select
                  className={styles.filterSelectMulti}
                  options={AGENT_TYPE_FILTER_OPTIONS}
                  value={AGENT_TYPE_FILTER_OPTIONS.filter(
                    (option) => templateAgentTypes.includes(option.value as AgentType),
                  )}
                  onChange={(value) => {
                    const selected = (value as MultiValue<SelectOption>) || [];
                    setTemplateAgentTypes(selected.map((option) => option.value as AgentType));
                  }}
                  placeholder={templateAgentTypes.length
                    ? `Selected: ${templateAgentTypes.length}`
                    : 'All agent types'}
                  isMulti
                  isSearchable
                  isClearable
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  components={filterMultiSelectComponents}
                  menuPortalTarget={typeof document === 'undefined' ? undefined : document.body}
                />
                <Select
                  className={styles.filterSelect}
                  options={STATUS_OPTIONS}
                  value={STATUS_OPTIONS.find((option) => option.value === templateStatus) || STATUS_OPTIONS[0]}
                  onChange={(value) => {
                    const selected = value as SingleValue<SelectOption>;
                    setTemplateStatus((selected?.value || 'all') as 'all' | 'enabled' | 'disabled');
                  }}
                  placeholder="All status"
                  menuPortalTarget={typeof document === 'undefined' ? undefined : document.body}
                />
                <Select
                  className={styles.filterSelectMulti}
                  options={companyOptions}
                  value={companyOptions.filter((option) => templateCompanyFilter.includes(option.value))}
                  onChange={(value) => {
                    const selected = (value as MultiValue<SelectOption>) || [];
                    setTemplateCompanyFilter(selected.map((option) => option.value));
                  }}
                  placeholder={templateCompanyFilter.length
                    ? `Selected: ${templateCompanyFilter.length}`
                    : 'All companies'}
                  isMulti
                  isSearchable
                  isClearable
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  components={filterMultiSelectComponents}
                  menuPortalTarget={typeof document === 'undefined' ? undefined : document.body}
                />
              </div>
            </div>

            <Panel className={styles.panel}>
              {isTemplatesLoading ? <Loader isPageLoading /> : null}
              {hasTemplateRows ? (
                <>
                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Agent type</th>
                          <th>Template name</th>
                          <th>Version</th>
                          <th>AI Model</th>
                          <th>Companies</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedTemplates.map((template) => {
                          const current = recordToVersion(template);
                          return (
                            <tr key={template.id}>
                              <td>{formatAgentType(template.agentType)}</td>
                              <td>{current.templateName}</td>
                              <td><span className={styles.versionPill}>{`V${current.versionNumber}`}</span></td>
                              <td>{current.aiModel}</td>
                              <td>
                                <div className={styles.companyTokens}>
                                  {companyTokens(template.companyAssignments).map((company) => (
                                    <span key={company} className={styles.companyToken}>{company}</span>
                                  ))}
                                </div>
                              </td>
                              <td>
                                <span className={current.enabled ? styles.enabled : styles.disabled}>
                                  {current.enabled ? 'Enabled' : 'Disabled'}
                                </span>
                              </td>
                              <td>
                                <div className={styles.actions}>
                                  <Tooltip hint="Edit template">
                                    <IconButton
                                      icon={<PencilIcon />}
                                      ariaLabel="Edit template"
                                      onClick={() => {
                                        setSelectedTemplateId(template.id);
                                        setScreen('edit');
                                      }}
                                      rounded
                                    />
                                  </Tooltip>
                                  <Tooltip hint="Companies access">
                                    <IconButton
                                      icon={<CompaniesIcon />}
                                      ariaLabel="Companies access"
                                      onClick={() => {
                                        setSelectedTemplateId(template.id);
                                        setCompaniesDraft(template.companyAssignments);
                                        setPendingCompanies([]);
                                        setCompaniesModalOpen(true);
                                      }}
                                      rounded
                                    />
                                  </Tooltip>
                                  <Tooltip hint="Version history">
                                    <IconButton
                                      icon={<UndoIcon />}
                                      ariaLabel="Version history"
                                      onClick={() => {
                                        setSelectedTemplateId(template.id);
                                        setScreen('history');
                                      }}
                                      rounded
                                    />
                                  </Tooltip>
                                  <Tooltip hint="Delete template">
                                    <IconButton
                                      icon={<TrashIcon />}
                                      ariaLabel="Delete template"
                                      onClick={() => {
                                        setSelectedTemplateId(template.id);
                                        setDeleteModalOpen(true);
                                      }}
                                      rounded
                                    />
                                  </Tooltip>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <CustomPagination
                    currentPage={templatePage}
                    totalPages={templateTotalPages}
                    onPageChange={setTemplatePage}
                    className={styles.pagination}
                  />
                </>
              ) : null}
              {shouldShowTemplatesEmptyState ? (
                <EmptyList text="You have not created any templates yet.">
                  <Button
                    title="Add new template"
                    startIcon={<PlusIcon />}
                    onClick={() => {
                      setDraft(emptyDraft());
                      setCreateStep(1);
                      setScreen('create');
                    }}
                  />
                </EmptyList>
              ) : null}
            </Panel>
          </>
        ) : null}

        {screen === 'create' ? (
          <div className={styles.editor}>
            <div className={styles.stepper} aria-label="Create template steps">
              {['Agent type', 'Base template', 'Settings', 'Companies'].map((label, index) => {
                const stepClassName = [
                  styles.step,
                  createStep === index + 1 ? styles.stepActive : '',
                  createStep > index + 1 ? styles.stepDone : '',
                ].filter(Boolean).join(' ');

                return (
                  <div key={label} className={styles.stepGroup}>
                    <div className={stepClassName}>
                      <span>{index + 1}</span>
                      <strong>{label}</strong>
                    </div>
                    {index < 3 ? <span className={styles.stepChevron} aria-hidden>›</span> : null}
                  </div>
                );
              })}
            </div>

            {createStep === 1 ? (
              <div className={styles.agentTypeStep}>
                <p className={styles.stepHint}>Select an agent type to get started</p>
                <div className={styles.agentTypeGrid}>
                  {AGENT_TYPE_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={
                        `${styles.selectCard} ${draft.agentType === option.id ? styles.selectCardActive : ''}`
                      }
                      onClick={() => {
                        setDraft((prev) => ({
                          ...prev,
                          agentType: option.id,
                          baseTemplateId: '',
                          description: option.description,
                        }));
                        setBaseSearch('');
                        setCreateStep(2);
                      }}
                    >
                      {agentTypeIcon(option.id)}
                      <h3>{option.title}</h3>
                      <p>{option.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {createStep === 2 ? (
              <div className={styles.baseTemplateStep}>
                <p className={styles.stepHint}>Choose the template on which your template will be based:</p>
                {shouldShowBaseTemplateBrowser ? (
                  <>
                    {isBaseTemplatesLoading ? <Loader isPageLoading /> : null}
                    {!isBaseTemplatesLoading ? (
                      <>
                        <div className={styles.searchField}>
                          <SearchIcon />
                          <input
                            value={baseSearch}
                            onChange={(e) => setBaseSearch(e.target.value)}
                            placeholder="Search"
                            aria-label="Search base templates"
                          />
                        </div>
                        <div className={styles.baseTableWrap}>
                          <table className={styles.baseTable}>
                            <thead>
                              <tr>
                                {([
                                  ['templateName', 'Name'],
                                  ['aiModel', 'AI Model'],
                                  ['versionNumber', 'Version'],
                                  ['minimumConfidence', 'Min confidence'],
                                  ['minimumEvidenceUrls', 'Min evidence URLs'],
                                ] as const).map(([field, label]) => (
                                  <th
                                    key={field}
                                    className={styles.sortableHeader}
                                    onClick={() => toggleBaseSort(field)}
                                    aria-sort={(() => {
                                      if (baseSortField !== field) {
                                        return 'none';
                                      }
                                      return baseSortDir === 'asc' ? 'ascending' : 'descending';
                                    })()}
                                  >
                                    <span>{label}</span>
                                    <span className={styles.sortIndicator}>
                                      {baseSortField !== field && '⇅'}
                                      {baseSortField === field && baseSortDir === 'asc' && '▲'}
                                      {baseSortField === field && baseSortDir === 'desc' && '▼'}
                                    </span>
                                  </th>
                                ))}
                                <th>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredBases.map(({ template, current }) => (
                                <tr
                                  key={template.id}
                                  className={
                                    `${styles.baseTableRow} ${
                                      draft.baseTemplateId === template.id ? styles.baseTableRowActive : ''
                                    }`
                                  }
                                  onClick={() => {
                                    setDraft(draftFromVersion(
                                      current,
                                      template.agentType,
                                      template.companyAssignments,
                                      template.id,
                                    ));
                                    setCreateStep(3);
                                  }}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setDraft(draftFromVersion(
                                        current,
                                        template.agentType,
                                        template.companyAssignments,
                                        template.id,
                                      ));
                                      setCreateStep(3);
                                    }
                                  }}
                                >
                                  <td className={styles.baseTableName}>{current.templateName}</td>
                                  <td>{current.aiModel}</td>
                                  <td>{`V${current.versionNumber}`}</td>
                                  <td>{current.minimumConfidence}</td>
                                  <td>{current.minimumEvidenceUrls}</td>
                                  <td className={styles.baseTableDesc}>{current.description || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {!filteredBases.length ? (
                          <p className={styles.emptyInline}>No templates match your search.</p>
                        ) : null}
                      </>
                    ) : null}
                  </>
                ) : (
                  <button
                    type="button"
                    className={styles.baseCard}
                    onClick={() => {
                      setDraft((prev) => ({
                        ...emptyDraft(),
                        agentType: prev.agentType,
                        baseTemplateId: '',
                      }));
                      setCreateStep(3);
                    }}
                  >
                    <div className={styles.baseHead}>
                      <h3>Start from scratch</h3>
                    </div>
                    <p>No base templates exist for this agent type yet. Continue with empty settings.</p>
                  </button>
                )}
              </div>
            ) : null}

            {(createStep === 3 || createStep === 4) ? (
              <div className={styles.formGrid}>
                <Panel className={`${styles.panel} ${styles.sectionPanel}`}>
                  <div className={styles.sectionPanelHeader}><h2>General</h2></div>
                  <div className={`${styles.formStack} ${styles.sectionPanelBody}`}>
                    <label className={styles.field} htmlFor="create-template-name">
                      <span>Template name</span>
                      <input
                        id="create-template-name"
                        value={draft.templateName}
                        onChange={(e) => setDraft((prev) => ({ ...prev, templateName: e.target.value }))}
                      />
                      {errors.templateName ? <small>{errors.templateName}</small> : null}
                    </label>
                    <label className={styles.field} htmlFor="create-template-description">
                      <span>Description (shown in Value Chain template picker)</span>
                      <textarea
                        id="create-template-description"
                        value={draft.description}
                        onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        placeholder="Explain what this template does for company users"
                      />
                    </label>
                    <label className={styles.field} htmlFor="create-agent-model">
                      <span>AI Model</span>
                      <select
                        id="create-agent-model"
                        value={draft.agentModelId ?? ''}
                        disabled={isAgentModelsLoading || !agentModelOptions.length}
                        onChange={(e) => {
                          const nextId = Number(e.target.value);
                          const selected = agentModelOptions.find((model) => model.id === nextId);
                          setDraft((prev) => ({
                            ...prev,
                            agentModelId: Number.isFinite(nextId) ? nextId : null,
                            aiModel: selected?.name || '',
                          }));
                        }}
                      >
                        {!agentModelOptions.length ? (
                          <option value="">
                            {isAgentModelsLoading ? 'Loading models…' : 'No models available'}
                          </option>
                        ) : null}
                        {agentModelOptions.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.enabled ? model.name : `${model.name} (disabled)`}
                          </option>
                        ))}
                      </select>
                      {isAgentModelsError ? <small>Failed to load AI models.</small> : null}
                      {errors.aiModel ? <small>{errors.aiModel}</small> : null}
                    </label>
                    <div className={styles.toggleRow}>
                      <span>Enabled</span>
                      <Toggle
                        name="enabled"
                        value="enabled"
                        checked={draft.enabled}
                        onChange={(e) => setDraft((prev) => ({ ...prev, enabled: e.target.checked }))}
                      />
                    </div>
                    <label className={styles.field} htmlFor="create-minimum-confidence">
                      <span>Minimum confidence (0.00-1.00)</span>
                      <input
                        id="create-minimum-confidence"
                        value={draft.minimumConfidence}
                        onChange={(e) => setDraft((prev) => ({
                          ...prev,
                          minimumConfidence: e.target.value,
                        }))}
                      />
                      {errors.minimumConfidence ? <small>{errors.minimumConfidence}</small> : null}
                    </label>
                    <label className={styles.field} htmlFor="create-minimum-evidence-urls">
                      <span>Minimum evidence URLs</span>
                      <input
                        id="create-minimum-evidence-urls"
                        value={draft.minimumEvidenceUrls}
                        onChange={(e) => setDraft((prev) => ({
                          ...prev,
                          minimumEvidenceUrls: e.target.value,
                        }))}
                      />
                      {errors.minimumEvidenceUrls ? <small>{errors.minimumEvidenceUrls}</small> : null}
                    </label>
                    <label className={styles.checkbox} htmlFor="create-supplier-lookup-first-step">
                      <input
                        id="create-supplier-lookup-first-step"
                        type="checkbox"
                        checked={draft.supplierLookupFirstStep}
                        onChange={(e) => setDraft((prev) => ({
                          ...prev,
                          supplierLookupFirstStep: e.target.checked,
                        }))}
                      />
                      <span>Company’s Supplier Lookup First Step</span>
                    </label>
                    {draft.agentType === 'bom' ? (
                      <AllowInferredConnectionsField
                        id="create-allow-inferred-edges"
                        checked={draft.allowInferredEdges}
                        onChange={(checked) => setDraft((prev) => ({ ...prev, allowInferredEdges: checked }))}
                      />
                    ) : null}
                    {draft.agentType === 'supplier-investigation' ? (
                      <label className={styles.checkbox} htmlFor="create-run-verification-after-discovery">
                        <input
                          id="create-run-verification-after-discovery"
                          type="checkbox"
                          checked={draft.runVerificationAfterDiscovery}
                          onChange={(e) => setDraft((prev) => ({
                            ...prev,
                            runVerificationAfterDiscovery: e.target.checked,
                          }))}
                        />
                        <span>Run verification after discovery</span>
                      </label>
                    ) : null}
                    {createStep === 4 ? (
                      <>
                        <div className={styles.infoBox}>
                          If no companies are assigned, this template will be visible to all companies.
                        </div>
                        <div className={styles.inlineAction}>
                          <Select
                            className={styles.companySelect}
                            options={createAssignableCompanyOptions}
                            value={companyOptions.filter((option) => pendingCompanies.includes(option.value))}
                            onChange={(value) => {
                              const selected = (value as MultiValue<SelectOption>) || [];
                              setPendingCompanies(selected.map((option) => option.value));
                            }}
                            placeholder={createAssignableCompanyOptions.length
                              ? 'Select companies to add'
                              : 'All companies already assigned'}
                            isMulti
                            isSearchable
                            isClearable
                            isDisabled={!createAssignableCompanyOptions.length}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            components={checkboxMultiSelectComponents}
                            menuPortalTarget={menuPortalTarget}
                          />
                          <Button
                            title="Add"
                            disabled={!pendingCompanies.length}
                            onClick={() => {
                              setDraft((prev) => ({
                                ...prev,
                                companyAssignments: addPendingCompanies(prev.companyAssignments, pendingCompanies),
                              }));
                              setPendingCompanies([]);
                            }}
                          />
                        </div>
                        <div className={styles.assignmentList}>
                          {draft.companyAssignments.length ? draft.companyAssignments.map((item) => (
                            <div key={item.companyId} className={styles.assignmentRow}>
                              <div>
                                <strong>{item.companyName}</strong>
                                {item.assignedAt ? (
                                  <span>{`${formatDateTime(item.assignedAt)} · ${item.assignedBy}`}</span>
                                ) : null}
                              </div>
                              <button
                                type="button"
                                onClick={() => setDraft((prev) => ({
                                  ...prev,
                                  companyAssignments: prev.companyAssignments.filter(
                                    (assignment) => assignment.companyId !== item.companyId,
                                  ),
                                }))}
                              >
                                Remove
                              </button>
                            </div>
                          )) : (
                            <p className={styles.emptyInline}>
                              No companies assigned yet. Template will be available to all companies.
                            </p>
                          )}
                        </div>
                      </>
                    ) : null}
                  </div>
                </Panel>
                <Panel className={`${styles.panel} ${styles.sectionPanel}`}>
                  <div className={styles.sectionPanelHeader}><h2>Prompt</h2></div>
                  <div className={styles.sectionPanelBody}>
                    <label className={styles.field} htmlFor="create-prompt">
                      <textarea
                        id="create-prompt"
                        value={draft.prompt}
                        onChange={(e) => setDraft((prev) => ({ ...prev, prompt: e.target.value }))}
                        rows={18}
                      />
                      {errors.prompt ? <small>{errors.prompt}</small> : null}
                    </label>
                  </div>
                </Panel>
              </div>
            ) : null}

            <div className={`${styles.footerActions} ${createStep === 1 ? styles.footerActionsEnd : ''}`}>
              {createStep > 1 ? (
                <div className={styles.footerActionsStart}>
                  <Button
                    title="Cancel"
                    variant={BUTTON_VARIANT.cancel}
                    onClick={() => {
                      setDraft(emptyDraft());
                      setCreateStep(1);
                      setBaseSearch('');
                      setPendingCompanies([]);
                      setErrors({});
                      setScreen('templates');
                    }}
                  />
                  <Button
                    title="Prev"
                    variant={BUTTON_VARIANT.secondary}
                    onClick={() => setCreateStep((prev) => prev - 1)}
                  />
                </div>
              ) : <span />}
              {createStep < 4 ? (
                <Button
                  title="Next"
                  disabled={
                    (createStep === 1 && !draft.agentType)
                    || (createStep === 2 && baseTemplateRows.length > 0 && !draft.baseTemplateId)
                  }
                  onClick={() => {
                    if (createStep === 1 && !draft.agentType) return;
                    if (createStep === 2 && baseTemplateRows.length > 0 && !draft.baseTemplateId) return;
                    if (createStep === 3 && !validateDraft()) return;
                    setCreateStep((prev) => prev + 1);
                  }}
                />
              ) : (
                <Button
                  title="Create template"
                  disabled={isTemplateMutationPending}
                  onClick={() => {
                    if (validateDraft()) {
                      createTemplate().catch(() => undefined);
                    }
                  }}
                />
              )}
            </div>
          </div>
        ) : null}

        {screen === 'edit' && selectedTemplate && selectedCurrentVersion && isTemplateDetailLoading ? (
          <Loader isPageLoading />
        ) : null}

        {screen === 'edit' && selectedTemplate && selectedCurrentVersion && !isTemplateDetailLoading ? (
          <div className={styles.editor}>
            <div className={styles.breadcrumb}>
              {'Agent Management / Templates / '}
              {formatAgentType(selectedTemplate.agentType)}
              {' / '}
              {selectedCurrentVersion.templateName}
              {` v${selectedCurrentVersion.versionNumber}`}
            </div>
            <div className={styles.metaBar}>
              <div className={styles.metaItem}>
                <span>Status:</span>
                <strong className={draft.enabled ? styles.enabled : styles.disabled}>
                  {draft.enabled ? 'Enabled' : 'Disabled'}
                </strong>
              </div>
              <div className={styles.metaItem}>
                <span>Version:</span>
                <strong>{`V${selectedCurrentVersion.versionNumber}`}</strong>
              </div>
              <div className={styles.metaItem}>
                <span>Agent type:</span>
                <strong>{formatAgentType(selectedTemplate.agentType)}</strong>
              </div>
              <div className={styles.metaItem}>
                <span>ID:</span>
                <strong>{selectedTemplate.id}</strong>
              </div>
              <div className={styles.metaItem}>
                <span>Created:</span>
                <strong>{formatDateTime(selectedCurrentVersion.createdAt)}</strong>
              </div>
              <div className={styles.metaItem}>
                <span>Saved by:</span>
                <strong>{selectedCurrentVersion.savedBy}</strong>
              </div>
            </div>

            <div className={styles.formGrid}>
              <Panel className={`${styles.panel} ${styles.sectionPanel}`}>
                <div className={styles.sectionPanelHeader}>
                  <h2>General</h2>
                  <div className={styles.sectionPanelHeaderActions}>
                    <Tooltip hint="Version history">
                      <IconButton
                        className={styles.sectionPanelHeaderAction}
                        icon={<HistoryIcon />}
                        ariaLabel="Version history"
                        onClick={() => setScreen('history')}
                        rounded
                        size="large"
                      />
                    </Tooltip>
                    <Tooltip hint="Companies access">
                      <IconButton
                        className={styles.sectionPanelHeaderAction}
                        icon={<CompaniesIcon />}
                        ariaLabel="Companies access"
                        onClick={() => {
                          setCompaniesDraft(selectedTemplate.companyAssignments);
                          setPendingCompanies([]);
                          setCompaniesModalOpen(true);
                        }}
                        rounded
                        size="large"
                      />
                    </Tooltip>
                  </div>
                </div>
                <div className={`${styles.formStack} ${styles.sectionPanelBody}`}>
                  <label className={styles.field} htmlFor="edit-template-name">
                    <span>Template name</span>
                    <input
                      id="edit-template-name"
                      value={draft.templateName}
                      onChange={(e) => setDraft((prev) => ({ ...prev, templateName: e.target.value }))}
                    />
                  </label>
                  <label className={styles.field} htmlFor="edit-template-description">
                    <span>Description (shown in Value Chain template picker)</span>
                    <textarea
                      id="edit-template-description"
                      value={draft.description}
                      onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      placeholder="Explain what this template does for company users"
                    />
                  </label>
                  <label className={styles.field} htmlFor="edit-agent-model">
                    <span>AI Model</span>
                    <select
                      id="edit-agent-model"
                      value={draft.agentModelId ?? ''}
                      disabled={isAgentModelsLoading || !agentModelOptions.length}
                      onChange={(e) => {
                        const nextId = Number(e.target.value);
                        const selected = agentModelOptions.find((model) => model.id === nextId);
                        setDraft((prev) => ({
                          ...prev,
                          agentModelId: Number.isFinite(nextId) ? nextId : null,
                          aiModel: selected?.name || '',
                        }));
                      }}
                    >
                      {!agentModelOptions.length ? (
                        <option value="">
                          {isAgentModelsLoading ? 'Loading models…' : 'No models available'}
                        </option>
                      ) : null}
                      {agentModelOptions.map((model) => (
                        <option key={model.id} value={model.id}>
                          {model.enabled ? model.name : `${model.name} (disabled)`}
                        </option>
                      ))}
                    </select>
                    {isAgentModelsError ? <small>Failed to load AI models.</small> : null}
                    {errors.aiModel ? <small>{errors.aiModel}</small> : null}
                  </label>
                  <div className={styles.toggleRow}>
                    <span>Enabled</span>
                    <Toggle
                      name="edit-enabled"
                      value="enabled"
                      checked={draft.enabled}
                      onChange={(e) => setDraft((prev) => ({ ...prev, enabled: e.target.checked }))}
                    />
                  </div>
                  <label className={styles.field} htmlFor="edit-minimum-confidence">
                    <span>Minimum confidence</span>
                    <input
                      id="edit-minimum-confidence"
                      value={draft.minimumConfidence}
                      onChange={(e) => setDraft((prev) => ({
                        ...prev,
                        minimumConfidence: e.target.value,
                      }))}
                    />
                    {errors.minimumConfidence ? <small>{errors.minimumConfidence}</small> : null}
                  </label>
                  <label className={styles.field} htmlFor="edit-minimum-evidence-urls">
                    <span>Minimum evidence URLs</span>
                    <input
                      id="edit-minimum-evidence-urls"
                      value={draft.minimumEvidenceUrls}
                      onChange={(e) => setDraft((prev) => ({
                        ...prev,
                        minimumEvidenceUrls: e.target.value,
                      }))}
                    />
                    {errors.minimumEvidenceUrls ? <small>{errors.minimumEvidenceUrls}</small> : null}
                  </label>
                  <label className={styles.checkbox} htmlFor="edit-supplier-lookup-first-step">
                    <input
                      id="edit-supplier-lookup-first-step"
                      type="checkbox"
                      checked={draft.supplierLookupFirstStep}
                      onChange={(e) => setDraft((prev) => ({
                        ...prev,
                        supplierLookupFirstStep: e.target.checked,
                      }))}
                    />
                    <span>Company’s Supplier Lookup First Step</span>
                  </label>
                  {selectedTemplate.agentType === 'bom' ? (
                    <AllowInferredConnectionsField
                      id="edit-allow-inferred-edges"
                      checked={draft.allowInferredEdges}
                      onChange={(checked) => setDraft((prev) => ({ ...prev, allowInferredEdges: checked }))}
                    />
                  ) : null}
                  {selectedTemplate.agentType === 'supplier-investigation' ? (
                    <label className={styles.checkbox} htmlFor="edit-run-verification-after-discovery">
                      <input
                        id="edit-run-verification-after-discovery"
                        type="checkbox"
                        checked={draft.runVerificationAfterDiscovery}
                        onChange={(e) => setDraft((prev) => ({
                          ...prev,
                          runVerificationAfterDiscovery: e.target.checked,
                        }))}
                      />
                      <span>Run verification after discovery</span>
                    </label>
                  ) : null}
                </div>
              </Panel>
              <Panel className={`${styles.panel} ${styles.sectionPanel}`}>
                <div className={styles.sectionPanelHeader}><h2>Prompt</h2></div>
                <div className={styles.sectionPanelBody}>
                  <label className={styles.field} htmlFor="edit-prompt">
                    <textarea
                      id="edit-prompt"
                      rows={18}
                      value={draft.prompt}
                      onChange={(e) => setDraft((prev) => ({ ...prev, prompt: e.target.value }))}
                    />
                  </label>
                </div>
              </Panel>
            </div>

            <div className={styles.footerActions}>
              <Button title="Cancel" variant={BUTTON_VARIANT.cancel} onClick={() => setScreen('templates')} />
              <Button
                title="Save"
                disabled={isTemplateMutationPending}
                onClick={() => {
                  if (!validateDraft()) {
                    toast.error('Fix the highlighted fields before saving.');
                    return;
                  }
                  if (!selectedCurrentVersion || !hasVersionChanges(draft, selectedCurrentVersion)) {
                    toast.info('No changes to save. A new version was not created.');
                    return;
                  }
                  setSaveModalOpen(true);
                }}
              />
            </div>
          </div>
        ) : null}

        {screen === 'history' && selectedTemplate && isTemplateVersionsLoading ? (
          <Loader isPageLoading />
        ) : null}

        {screen === 'history' && selectedTemplate && !isTemplateVersionsLoading && historyAfter && historyBefore ? (
          <div className={styles.historyLayout}>
            <p className={styles.breadcrumb}>
              {'Agent Management / Templates / '}
              {formatAgentType(selectedTemplate.agentType)}
              {' / '}
              {selectedTemplate.templateName}
              {` v${selectedTemplate.currentVersionNumber}`}
            </p>

            <div className={styles.historyMetaRow}>
              <div className={styles.historyExplainer}>
                <p>
                  {historyBefore.versionNumber === historyAfter.versionNumber ? (
                    <>
                      {`Showing version V${historyAfter.versionNumber}. `}
                      Select another version in Compare to to see differences.
                    </>
                  ) : (
                    <>
                      {`Comparing selected version V${historyAfter.versionNumber} `}
                      {`with V${historyBefore.versionNumber}.`}
                    </>
                  )}
                </p>
                {historyBefore.versionNumber !== historyAfter.versionNumber ? (
                  <ul className={styles.historyExplainerLegend}>
                    <li>
                      <span className={`${styles.historyExplainerSwatch} ${styles.diffRemoved}`}>
                        V
                        {historyBefore.versionNumber}
                      </span>
                      <span>{`Red (strikethrough) = Compare to version V${historyBefore.versionNumber}`}</span>
                    </li>
                    <li>
                      <span className={`${styles.historyExplainerSwatch} ${styles.diffAdded}`}>
                        V
                        {historyAfter.versionNumber}
                      </span>
                      <span>{`Green = Selected version V${historyAfter.versionNumber}`}</span>
                    </li>
                  </ul>
                ) : null}
              </div>

              <div className={styles.historyCompareActions}>
                <label className={styles.historyCompareRow} htmlFor="history-compare-to">
                  <span>Compare to</span>
                  <select
                    id="history-compare-to"
                    value={historyCompareTo || ''}
                    onChange={(e) => setHistoryCompareTo(Number(e.target.value) || null)}
                  >
                    {compareOptions.map((version) => (
                      <option key={version.versionNumber} value={version.versionNumber}>
                        {`V${version.versionNumber}`}
                      </option>
                    ))}
                  </select>
                </label>
                <Button
                  title={`Restore as V${selectedTemplate.currentVersionNumber + 1}`}
                  disabled={
                    !currentHistoryVersion
                    || currentHistoryVersion.versionNumber === selectedTemplate.currentVersionNumber
                  }
                  onClick={() => setRestoreModalOpen(true)}
                />
              </div>
            </div>

            <div className={styles.historyContent}>
              <div className={styles.historyMain}>
                <div className={styles.formGrid}>
                  <Panel className={`${styles.panel} ${styles.sectionPanel}`}>
                    <div className={styles.sectionPanelHeader}><h2>General</h2></div>
                    <div className={`${styles.formStack} ${styles.sectionPanelBody}`}>
                      <div className={styles.historyField}>
                        <span>Template name</span>
                        <div className={styles.historyFieldValue}>
                          <DiffText before={historyBefore.templateName} after={historyAfter.templateName} />
                        </div>
                      </div>
                      <div className={styles.historyField}>
                        <span>Description (Value Chain picker)</span>
                        <div className={styles.historyFieldValue}>
                          <DiffText
                            before={historyBefore.description}
                            after={historyAfter.description}
                            multiline
                          />
                        </div>
                      </div>
                      <div className={styles.historyField}>
                        <span>AI Model</span>
                        <div className={styles.historyFieldValue}>
                          <DiffText before={historyBefore.aiModel} after={historyAfter.aiModel} />
                        </div>
                      </div>
                      <div className={styles.historyField}>
                        <span>Enabled</span>
                        <div className={styles.historyToggleRow}>
                          <Toggle
                            name="history-enabled"
                            value="enabled"
                            checked={historyAfter.enabled}
                            disabled
                            onChange={() => undefined}
                          />
                          <small>Make this template available for use by assigned companies</small>
                        </div>
                        {historyBefore.enabled !== historyAfter.enabled ? (
                          <div className={styles.historyFieldHint}>
                            <DiffText
                              before={historyBefore.enabled ? 'Enabled' : 'Disabled'}
                              after={historyAfter.enabled ? 'Enabled' : 'Disabled'}
                            />
                          </div>
                        ) : null}
                      </div>
                      <div className={styles.historyField}>
                        <span>Minimum confidence level (0.00 to 1.00)</span>
                        <div className={styles.historyFieldValue}>
                          <DiffText
                            before={historyBefore.minimumConfidence}
                            after={historyAfter.minimumConfidence}
                          />
                        </div>
                      </div>
                      <div className={styles.historyField}>
                        <span>Minimum evidence URLs</span>
                        <div className={styles.historyFieldValue}>
                          <DiffText
                            before={historyBefore.minimumEvidenceUrls}
                            after={historyAfter.minimumEvidenceUrls}
                          />
                        </div>
                      </div>
                      <label className={styles.checkbox} htmlFor="history-supplier-lookup-first-step">
                        <input
                          id="history-supplier-lookup-first-step"
                          type="checkbox"
                          checked={historyAfter.supplierLookupFirstStep}
                          readOnly
                        />
                        <span>Company’s Supplier Lookup First Step</span>
                      </label>
                      {selectedTemplate.agentType === 'bom' ? (
                        <AllowInferredConnectionsField
                          id="history-allow-inferred-edges"
                          checked={historyAfter.allowInferredEdges}
                          readOnly
                        />
                      ) : null}
                      {selectedTemplate.agentType === 'supplier-investigation' ? (
                        <label className={styles.checkbox} htmlFor="history-run-verification-after-discovery">
                          <input
                            id="history-run-verification-after-discovery"
                            type="checkbox"
                            checked={historyAfter.runVerificationAfterDiscovery}
                            readOnly
                          />
                          <span>Run verification after discovery</span>
                        </label>
                      ) : null}
                    </div>
                  </Panel>
                  <Panel className={`${styles.panel} ${styles.sectionPanel}`}>
                    <div className={styles.sectionPanelHeader}><h2>Prompt</h2></div>
                    <div className={styles.sectionPanelBody}>
                      <div className={styles.historyPrompt}>
                        <DiffText before={historyBefore.prompt} after={historyAfter.prompt} multiline />
                      </div>
                    </div>
                  </Panel>
                </div>
              </div>

              <Panel className={`${styles.panel} ${styles.historySidebar}`}>
                <h3 className={styles.historySidebarTitle}>All versions</h3>
                <div className={styles.versionList}>
                  {historyVersions.map((version) => {
                    const isCurrent = version.versionNumber === selectedTemplate.currentVersionNumber;
                    const isSelected = historyAfter.versionNumber === version.versionNumber;
                    const isCompareBaseline = historyBefore.versionNumber === version.versionNumber
                      && historyBefore.versionNumber !== historyAfter.versionNumber;
                    const fallbackCompareVersion = historyVersions.find(
                      (item) => item.versionNumber !== version.versionNumber,
                    );
                    return (
                      <button
                        key={version.versionNumber}
                        type="button"
                        className={[
                          styles.versionCard,
                          isSelected ? styles.versionCardAdded : '',
                          isCompareBaseline ? styles.versionCardRemoved : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => {
                          setHistoryVersion(version.versionNumber);
                          const previousVersion = historyVersions.find(
                            (item) => item.versionNumber === version.versionNumber - 1,
                          );
                          setHistoryCompareTo(
                            previousVersion?.versionNumber
                            || fallbackCompareVersion?.versionNumber
                            || null,
                          );
                        }}
                      >
                        <div className={styles.versionCardHead}>
                          <span className={[
                            styles.versionPill,
                            isSelected ? styles.versionPillAdded : '',
                            isCompareBaseline ? styles.versionPillRemoved : '',
                          ].filter(Boolean).join(' ')}
                          >
                            {`V${version.versionNumber}`}
                          </span>
                          {isCurrent ? <span className={styles.currentBadge}>Current</span> : null}
                          {isSelected ? <span className={styles.versionRoleAdded}>Selected</span> : null}
                          {isCompareBaseline ? <span className={styles.versionRoleRemoved}>Compare to</span> : null}
                        </div>
                        <span>{`Created: ${formatDateTime(version.createdAt)}`}</span>
                        <span>{`Saved by: ${version.savedBy}`}</span>
                      </button>
                    );
                  })}
                </div>
              </Panel>
            </div>
          </div>
        ) : null}

        {screen === 'analytics' ? (
          <div className={styles.analyticsLayout}>
            <div className={styles.analyticsSummaryGrid}>
              {AGENT_TYPES.map((agentType) => (
                <Panel key={agentType} className={styles.panel}>
                  <h2>{formatAgentType(agentType)}</h2>
                  <div className={styles.metricsGrid}>
                    {analyticsCards(agentType).map(([label, value]) => (
                      <div key={label} className={styles.metricCard}>
                        <span>{label}</span>
                        <strong>{value}</strong>
                      </div>
                    ))}
                  </div>
                </Panel>
              ))}
            </div>

            <Panel className={styles.panel}>
              <div className={styles.analyticsToolbar}>
                <div className={styles.inlineAction}>
                  <select
                    value={analyticsAgentType}
                    onChange={(e) => setAnalyticsAgentType(e.target.value as 'all' | AgentType)}
                    className={styles.select}
                  >
                    <option value="all">All agent types</option>
                    {AGENT_TYPE_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>{option.title}</option>
                    ))}
                  </select>
                  <select
                    value={analyticsCompany}
                    onChange={(e) => setAnalyticsCompany(e.target.value)}
                    className={styles.select}
                  >
                    <option value="all">All companies</option>
                    {MOCK_ANALYTICS_COMPANIES.map((company) => (
                      <option key={company} value={company}>{company}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.inlineAction}>
                  <Button
                    title="Export company summary"
                    startIcon={<DownloadIcon />}
                    variant={BUTTON_VARIANT.secondary}
                    onClick={() => downloadCsv(
                      'company-summary.csv',
                      [
                        ['Agent Type', 'Company', 'Cost', 'Runs', 'Avg. time', 'Success rate'],
                        ...filteredSummary.map((item) => [
                          formatAgentType(item.agentType),
                          item.companyName,
                          formatCurrency(item.cost),
                          item.runs,
                          formatDuration(item.averageTimeSeconds),
                          `${item.successRate.toFixed(1)}%`,
                        ]),
                      ],
                    )}
                  />
                  <Button
                    title="Export run details"
                    startIcon={<DownloadIcon />}
                    onClick={() => downloadCsv(
                      'run-details.csv',
                      [
                        [
                          'Agent Type',
                          'Company',
                          'Run ID',
                          'Tokens',
                          'Cost',
                          'Status',
                          'Message',
                          'Template Version',
                          'Run At',
                        ],
                        ...filteredRuns.map((item) => [
                          formatAgentType(item.agentType),
                          item.companyName,
                          item.id,
                          item.tokens,
                          item.cost.toFixed(6),
                          item.status,
                          item.message,
                          item.templateVersion,
                          formatDateTime(item.runAt),
                        ]),
                      ],
                    )}
                  />
                </div>
              </div>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Agent Type</th>
                      <th>Company</th>
                      <th>Cost</th>
                      <th>Runs</th>
                      <th>Avg. time</th>
                      <th>Success rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedSummary.map((item) => (
                      <tr key={item.id}>
                        <td>{formatAgentType(item.agentType)}</td>
                        <td>{item.companyName}</td>
                        <td>{formatCurrency(item.cost)}</td>
                        <td>{item.runs}</td>
                        <td>{formatDuration(item.averageTimeSeconds)}</td>
                        <td>
                          <span className={item.successRate >= 90 ? styles.enabled : styles.disabled}>
                            {`${item.successRate.toFixed(1)}%`}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <CustomPagination
                currentPage={analyticsPage}
                totalPages={analyticsTotalPages}
                onPageChange={setAnalyticsPage}
                className={styles.pagination}
              />
            </Panel>
          </div>
        ) : null}

        <Modal
          open={companiesModalOpen}
          onClose={() => {
            setCompaniesModalOpen(false);
            setPendingCompanies([]);
          }}
          onReject={() => {
            setCompaniesModalOpen(false);
            setPendingCompanies([]);
          }}
          onConfirm={async () => {
            const saved = await updateCompanyAssignments(companiesDraft);
            if (!saved) return;
            setCompaniesModalOpen(false);
            setPendingCompanies([]);
          }}
          title="Companies access"
          description="Company assignments update immediately on the active template. They do not create a new version."
          confirmTitle="Save"
          className={styles.companiesModal}
        >
          <div className={styles.modalContent}>
            <div className={styles.inlineAction}>
              <Select
                className={styles.companySelect}
                options={modalAssignableCompanyOptions}
                value={companyOptions.filter((option) => pendingCompanies.includes(option.value))}
                onChange={(value) => {
                  const selected = (value as MultiValue<SelectOption>) || [];
                  setPendingCompanies(selected.map((option) => option.value));
                }}
                placeholder={modalAssignableCompanyOptions.length
                  ? 'Select companies to add'
                  : 'All companies already assigned'}
                isMulti
                isSearchable
                isClearable
                isDisabled={!modalAssignableCompanyOptions.length}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={checkboxMultiSelectComponents}
                menuPortalTarget={menuPortalTarget}
              />
              <Button
                title="Add"
                disabled={!pendingCompanies.length}
                onClick={() => {
                  setCompaniesDraft((prev) => addPendingCompanies(prev, pendingCompanies));
                  setPendingCompanies([]);
                }}
              />
            </div>
            <div className={styles.assignmentList}>
              {companiesDraft.length ? companiesDraft.map((item) => (
                <div key={item.companyId} className={styles.assignmentRow}>
                  <div>
                    <strong>{item.companyName}</strong>
                    {item.assignedAt ? (
                      <span>{`${formatDateTime(item.assignedAt)} · ${item.assignedBy}`}</span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCompaniesDraft((prev) => prev.filter(
                      (assignment) => assignment.companyId !== item.companyId,
                    ))}
                  >
                    Remove
                  </button>
                </div>
              )) : (
                <p className={styles.emptyInline}>
                  No companies assigned. Template will be available to all companies.
                </p>
              )}
            </div>
          </div>
        </Modal>

        <Modal
          open={saveModalOpen}
          onClose={() => setSaveModalOpen(false)}
          onReject={() => setSaveModalOpen(false)}
          onConfirm={async () => {
            if (!selectedTemplate || !selectedCurrentVersion) {
              toast.error('Failed to save template. Please try again.');
              return;
            }
            if (!hasVersionChanges(draft, selectedCurrentVersion)) {
              setSaveModalOpen(false);
              toast.info('No changes to save. A new version was not created.');
              return;
            }
            const saved = await saveTemplateVersion(draft);
            if (!saved) return;
            setSaveModalOpen(false);
            goToTemplatesList(selectedTemplate.agentType);
          }}
          title={selectedTemplate ? `Save as V${selectedTemplate.currentVersionNumber + 1}?` : 'Save changes?'}
          description="This creates a new version of the template and activates it immediately."
          confirmTitle="Save"
          disabledConfirm={isTemplateMutationPending}
        />

        <Modal
          open={restoreModalOpen}
          onClose={() => setRestoreModalOpen(false)}
          onReject={() => setRestoreModalOpen(false)}
          onConfirm={async () => {
            if (!selectedTemplate || !currentHistoryVersion) {
              toast.error('Failed to restore template version. Please try again.');
              return;
            }
            const restored = await restoreTemplateVersion();
            if (!restored) return;
            setRestoreModalOpen(false);
            goToTemplatesList(selectedTemplate.agentType);
          }}
          title={selectedTemplate ? `Restore as V${selectedTemplate.currentVersionNumber + 1}?` : 'Restore version?'}
          description="Restoring creates a new active version cloned from the selected historical version."
          confirmTitle="Restore"
          disabledConfirm={isTemplateMutationPending}
        />

        <Modal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onReject={() => setDeleteModalOpen(false)}
          onConfirm={async () => {
            await deleteSelectedTemplate();
          }}
          title={selectedTemplate ? `Delete ${selectedTemplate.templateName}?` : 'Delete template?'}
          description="Deleting removes this template. Schedules using it may be paused or have their config cleared."
          confirmTitle="Delete"
          disabledConfirm={isTemplateMutationPending}
        />
      </div>
    </div>
  );
}

export default AgentsManagement;
